import {Component, EventEmitter, Input, Output, ChangeDetectionStrategy, inject, DestroyRef} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {NgClass, NgForOf, NgIf} from '@angular/common';
import {DateTime} from 'luxon';
import {Locale} from 'date-fns';
import {TranslatePipe} from '@ngx-translate/core';
import {BsDatepickerDirective, BsDatepickerInputDirective} from 'ngx-bootstrap/datepicker';
import {FormatPipeModule, ParseIsoPipeModule} from 'ngx-date-fns';
import {TabSwitcherComponent} from '../../../components/tab-switcher/tab-switcher.component';
import {FormatInTimeZonePipe} from '../../../pipes/format-in-time-zone/format-in-time-zone.pipe';
import {LocaleService} from '../../../services/locale-service/locale.service';
import {BERLIN_TZ} from '../event-calendar.constants';

@Component({
  selector: 'app-event-calendar-toolbar',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './event-calendar-toolbar.component.html',
  imports: [
    NgIf,
    NgForOf,
    NgClass,
    TranslatePipe,
    BsDatepickerDirective,
    BsDatepickerInputDirective,
    TabSwitcherComponent,
    FormatPipeModule,
    ParseIsoPipeModule,
    FormatInTimeZonePipe,
  ],
})
export class EventCalendarToolbarComponent {
  @Input() public currentStart: DateTime | undefined;
  @Input() public view: 'day' | 'week' | 'month' = 'day';

  /** 0 = thisWeek; n>0 = nextWeek(n) */
  @Output() public weekJump = new EventEmitter<number>();
  @Output() public dateChange = new EventEmitter<Date | undefined>();

  private localeService = inject(LocaleService);
  private destroyRef = inject(DestroyRef);

  public dfnsLocale: Locale = this.localeService.getDfnsLocale();
  protected readonly DateTime = DateTime;

  constructor() {
    this.localeService.localeChanged.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.dfnsLocale = this.localeService.getDfnsLocale();
    });
  }

  public get now(): DateTime { return DateTime.now().setZone(BERLIN_TZ); }

  public isThisWeek(d: DateTime): boolean { return d.hasSame(this.now, 'week'); }
  public isNextWeek(d: DateTime, n = 1): boolean {
    return d.startOf('week').hasSame(this.now.startOf('week').plus({weeks: n}), 'week');
  }
  public addWeeks(d: DateTime, n = 1): DateTime { return d.plus({weeks: n}); }
  public addDays(d: DateTime, n: number): DateTime { return d.plus({days: n}); }
  public startOfWeek(d: DateTime): DateTime { return d.startOf('week'); }
}
